import React, { useState, useEffect, useMemo } from 'react';
import { useZnoospaceProgram } from '../hooks/useChirpXProgram';
import { useOnboarding } from '../contexts/OnboardingContext';
import './ExplorePage.css';

const ExplorePage = ({ onClose }) => {
  const { address: walletAddress } = useOnboarding();
  const { solanaService } = useZnoospaceProgram(walletAddress);
  const [beams, setBeams] = useState([]);
  const [discoveredUsers, setDiscoveredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('trending');
  const [followVersion, setFollowVersion] = useState(0);

  // Discover users from the global network on mount
  useEffect(() => {
    if (solanaService?.discoverUsers) {
      discoverNetwork();
    }
  }, [solanaService]); // eslint-disable-line react-hooks/exhaustive-deps

  const discoverNetwork = async () => {
    setLoading(true);
    try {
      const result = await solanaService.discoverUsers();
      if (result.success) {
        setDiscoveredUsers(result.users || []);
        setBeams(result.beams || []);
      }
    } catch (error) {
      console.error('❌ Error discovering users:', error);
    } finally {
      setLoading(false);
    }
  };

  // Build user stats from beams
  const userStats = useMemo(() => {
    const statsMap = {};

    beams.forEach(beam => {
      const author = beam.author;
      if (!author || author === 'Unknown') return;

      if (!statsMap[author]) {
        statsMap[author] = {
          address: author,
          displayName: beam.displayName || `User ${author.slice(0, 8)}`,
          username: beam.username || `@${author.slice(0, 8)}...`,
          postCount: 0,
          totalLikes: 0,
          latestPost: null,
          latestTimestamp: 0,
        };
      }

      statsMap[author].postCount += 1;
      statsMap[author].totalLikes += (beam.likes || 0);

      if (beam.timestamp > statsMap[author].latestTimestamp) {
        statsMap[author].latestTimestamp = beam.timestamp;
        statsMap[author].latestPost = beam.content;
      }

      // Prefer richer display names
      if (beam.displayName && !beam.displayName.startsWith('User ')) {
        statsMap[author].displayName = beam.displayName;
      }
      if (beam.username && !beam.username.includes('...')) {
        statsMap[author].username = beam.username;
      }
    });

    return Object.values(statsMap);
  }, [beams]);

  // Sort users based on active category
  const sortedUsers = useMemo(() => {
    // Merge discovered users with any computed from beams
    const mergedMap = {};
    [...discoveredUsers, ...userStats].forEach(u => {
      if (!mergedMap[u.address]) {
        mergedMap[u.address] = { ...u };
      } else {
        const existing = mergedMap[u.address];
        existing.postCount = Math.max(existing.postCount, u.postCount);
        existing.totalLikes = Math.max(existing.totalLikes, u.totalLikes);
        if (u.latestTimestamp > existing.latestTimestamp) {
          existing.latestTimestamp = u.latestTimestamp;
          existing.latestPost = u.latestPost;
        }
        if (u.displayName && !u.displayName.startsWith('User ')) existing.displayName = u.displayName;
        if (u.username && !u.username.includes('...')) existing.username = u.username;
      }
    });
    let sorted = Object.values(mergedMap);

    // Don't filter out self — on a small network, show all users

    // Filter by search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      sorted = sorted.filter(u =>
        u.displayName.toLowerCase().includes(q) ||
        u.username.toLowerCase().includes(q) ||
        u.address.toLowerCase().includes(q)
      );
    }

    switch (activeCategory) {
      case 'trending':
        // Most active = most posts
        sorted.sort((a, b) => b.postCount - a.postCount);
        break;
      case 'popular':
        // Most liked
        sorted.sort((a, b) => b.totalLikes - a.totalLikes);
        break;
      case 'recent':
        // Most recently active
        sorted.sort((a, b) => b.latestTimestamp - a.latestTimestamp);
        break;
      default:
        break;
    }

    return sorted;
  }, [userStats, activeCategory, searchQuery, walletAddress]);

  // Trending hashtags from beams
  const trendingTags = useMemo(() => {
    const tagCounts = {};
    beams.forEach(beam => {
      const tags = (beam.content || '').match(/#(\w+)/g);
      if (tags) {
        tags.forEach(tag => {
          const t = tag.toLowerCase();
          tagCounts[t] = (tagCounts[t] || 0) + 1;
        });
      }
    });
    return Object.entries(tagCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([tag, count]) => ({ tag, count }));
  }, [beams]);

  const handleFollow = async (address) => {
    if (!solanaService) return;
    const status = solanaService.isFollowing(address);
    if (status?.isFollowing) {
      await solanaService.unfollowUser(address);
    } else {
      await solanaService.followUser(address);
    }
    setFollowVersion(v => v + 1);
  };

  const isUserFollowing = (address) => {
    if (!solanaService?.isFollowing) return false;
    // followVersion used to bust cache
    void followVersion;
    return solanaService.isFollowing(address)?.isFollowing || false;
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const diff = Date.now() - timestamp;
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const cleanContent = (content) => {
    if (!content) return '';
    return content
      .replace(/🔗\s*View on Solana Explorer:?\s*/gi, '')
      .replace(/https?:\/\/explorer\.solana\.com\/[^\s]*/gi, '')
      .trim()
      .slice(0, 120) + (content.length > 120 ? '...' : '');
  };

  return (
    <div className="explore-page">
      {/* Header */}
      <div className="explore-header">
        <button className="explore-back-btn" onClick={onClose}>
          ← Back
        </button>
        <h2>Explore</h2>
      </div>

      {/* Search Bar */}
      <div className="explore-search">
        <span className="search-icon">🔍</span>
        <input
          type="text"
          placeholder="Search users, hashtags..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="explore-search-input"
        />
        {searchQuery && (
          <button className="search-clear" onClick={() => setSearchQuery('')}>✕</button>
        )}
      </div>

      {/* Category Tabs */}
      <div className="explore-tabs">
        <button
          className={`explore-tab ${activeCategory === 'trending' ? 'active' : ''}`}
          onClick={() => setActiveCategory('trending')}
        >
          🔥 Most Active
        </button>
        <button
          className={`explore-tab ${activeCategory === 'popular' ? 'active' : ''}`}
          onClick={() => setActiveCategory('popular')}
        >
          ⭐ Most Liked
        </button>
        <button
          className={`explore-tab ${activeCategory === 'recent' ? 'active' : ''}`}
          onClick={() => setActiveCategory('recent')}
        >
          🕐 Recent
        </button>
      </div>

      {/* Trending Hashtags */}
      {trendingTags.length > 0 && !searchQuery && (
        <div className="explore-trending-tags">
          <h3>Trending</h3>
          <div className="trending-tags-list">
            {trendingTags.map(({ tag, count }) => (
              <button
                key={tag}
                className="trending-tag"
                onClick={() => setSearchQuery(tag)}
              >
                {tag} <span className="tag-count">{count}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* User List */}
      <div className="explore-users">
        {loading ? (
          <div className="explore-loading">
            <div className="cosmic-spinner"></div>
            <p>Scanning the cosmos for users...</p>
          </div>
        ) : sortedUsers.length === 0 ? (
          <div className="explore-empty">
            <h3>🌌 No users found</h3>
            <p>
              {searchQuery
                ? `No users match "${searchQuery}"`
                : 'Be the first to beam something into the cosmos!'}
            </p>
          </div>
        ) : (
          sortedUsers.map((user) => (
            <div key={user.address} className="explore-user-card">
              <div className="explore-user-avatar">
                <span>🚀</span>
              </div>
              <div className="explore-user-info">
                <div className="explore-user-header">
                  <div className="explore-user-names">
                    <span className="explore-user-display">
                      {user.displayName}
                      {user.address === walletAddress && <span className="explore-you-badge">You</span>}
                    </span>
                    <span className="explore-user-handle">{user.username}</span>
                  </div>
                  {user.address !== walletAddress && (
                    <button
                      className={`explore-follow-btn ${isUserFollowing(user.address) ? 'following' : ''}`}
                      onClick={() => handleFollow(user.address)}
                    >
                      {isUserFollowing(user.address) ? 'Following' : 'Follow'}
                    </button>
                  )}
                </div>
                <div className="explore-user-stats">
                  <span className="stat">📝 {user.postCount} post{user.postCount !== 1 ? 's' : ''}</span>
                  <span className="stat">✨ {user.totalLikes} like{user.totalLikes !== 1 ? 's' : ''}</span>
                  {user.latestTimestamp > 0 && (
                    <span className="stat">🕐 {formatTime(user.latestTimestamp)}</span>
                  )}
                </div>
                {user.latestPost && (
                  <div className="explore-user-preview">
                    {cleanContent(user.latestPost)}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ExplorePage;
